import { useEffect, useRef } from "react";

/**
 * Wire a wizard field into the page: hand its node up so validation can scroll
 * to it, and take focus when the step asks for it.
 *
 * Every field type needed the same two effects, and each one carried its own
 * copy with the dependency check switched off. Splitting them apart is what
 * makes the dependencies honest:
 *
 *   - registration depends on the path and the register function, nothing else;
 *   - focus depends only on whether this field is the one to focus, so a
 *     re-render while somebody is typing can never steal the caret back.
 *
 * `selector` is for fields whose focusable element sits inside the wrapper
 * (the Yes/No and option cards), rather than being the wrapper itself.
 */
export function useFieldRef({ path, registerRef, autoFocus = false, selector = null }) {
  const ref = useRef(null);

  useEffect(() => {
    if (registerRef) registerRef(path, ref);
  }, [path, registerRef]);

  useEffect(() => {
    if (!autoFocus) return undefined;
    const timer = setTimeout(() => {
      const node = ref.current;
      if (!node) return;
      const target = selector ? node.querySelector(selector) : node;
      target?.focus();
    }, 60);
    return () => clearTimeout(timer);
  }, [autoFocus, selector]);

  return ref;
}
